import * as React from "react";
import { Button, Input, notification, Space, Tooltip } from "antd";
import { AlertCircle, ChevronDown, ChevronUp, PlusCircle } from "react-feather";
import { linksOf, request, resourceLink } from "../../services/cpln";
import { randomName } from "../../services/utils";
import { parseLink } from "../../utils/linkParser/linkParser";
import { getTemplate } from "./kubeConfigTemplate";
import { ExternalLink } from "../../newcomponents/table/components/ExternalLink";
import { DOCS_URL } from "../../envVariables";
import { useAppSelector } from "../../store/hooks";
import { selectOrg } from "../../store/contextSlice";
import { Mk8sCluster } from "../../schema/types/mk8s/entity/mk8s";
import { FormLabel } from "../../components/antd/form/FormLabel";
import { KindSelect } from "../../components/antd/kindselect/KindSelect";
import { useDrawerContext } from "../../components/antd/drawerContext";
import { EmbeddedServiceAccountCreate } from "../serviceaccount/create/embedded";

interface Props {
  mk8s: Mk8sCluster;
}
export const KubeConfig: React.FC<Props> = ({ mk8s }) => {
  const selfLink = linksOf(mk8s).self!;

  const org = useAppSelector(selectOrg);
  const drawer = useDrawerContext();

  const [serviceAccountValue, setServiceAccountValue] = React.useState("");
  const [serviceAccName, setServiceAccName] = React.useState("");
  const [profileName, setProfileName] = React.useState("default");
  const [isLoading, setIsLoading] = React.useState(false);

  const [method, setMethod] = React.useState<"sa" | "pr" | null>(null);

  async function getCaCert() {
    try {
      const { data } = await request({ url: selfLink + "/-cacerts" });
      return data.cacerts;
    } catch (e) {
      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      notification.warning({ message: "Failed to get cacert", description: errorMessage });
    }
  }

  async function getKey(serviceAccountName: string, keyDescription: string) {
    try {
      const { data }: any = await request({
        method: "post",
        url: resourceLink("serviceaccount", serviceAccountName) + "/-addKey",
        body: { description: keyDescription },
      });
      return data.key;
    } catch (e) {
      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      notification.warning({ message: "Failed to get service account key", description: errorMessage });
      return "";
    }
  }

  async function createKubeConfig() {
    try {
      setIsLoading(true);
      const serviceAccountKey = await getKey(serviceAccName, randomName());
      if (!serviceAccountKey) {
        setIsLoading(false);
        return;
      }

      const caCert = await getCaCert();

      const _kubeConfig = getTemplate(
        org || "",
        mk8s.name,
        mk8s.alias || "",
        serviceAccName,
        serviceAccountKey,
        caCert,
        mk8s.status?.serverUrl || "",
      );

      downloadKubeConfig(_kubeConfig);
      setIsLoading(false);

      notification.success({
        message: "Success",
        description: "Generated & Downloaded kubeconfig",
      });
    } catch (e) {
      setIsLoading(false);
      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      notification.warning({
        message: "Failed",
        description: errorMessage,
      });
    }
  }

  async function createKubeConfigForProfile() {
    try {
      setIsLoading(true);

      const { data } = await request({
        method: "post",
        url: selfLink + "/-kubeconfig",
        body: { profile: profileName || "default" },
      });

      downloadKubeConfig(data.kubeconfig, data.fileName);
      setIsLoading(false);

      notification.success({
        message: "Success",
        description: "Downloaded kubeconfig",
      });
    } catch (e) {
      setIsLoading(false);
      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      notification.warning({
        message: "Failed",
        description: errorMessage,
      });
    }
  }

  function downloadKubeConfig(blob: string, filename?: string) {
    const a = document.createElement("a");
    a.style.display = "none";
    a.classList.add("cpln-temp-a");
    a.download = `${mk8s.name}-kubeconfig.yaml`;
    if (filename) {
      a.download = filename;
    }
    const file = new Blob([blob], { type: "text/yaml" });
    const href = URL.createObjectURL(file);
    a.href = href;
    a.click();
  }

  return (
    <div>
      <div className="mb-4 text-sm">
        Download a kubeconfig file to access your cluster using <code>kubectl</code>. You can either use a Service
        Account key for programmatic access (recommended for CI/CD and automation), or use your CPLN CLI profile for
        interactive local development.
      </div>
      <div
        className="mt-4 color-link cursor-pointer flex items-center gap-1"
        onClick={() => setMethod((v) => (v === "sa" ? null : "sa"))}
      >
        <FormLabel>Continue with Service Account</FormLabel>
        {method === "sa" ? (
          <ChevronUp style={{ width: 16, height: 16 }} />
        ) : (
          <ChevronDown style={{ width: 16, height: 16 }} />
        )}
      </div>
      {method === "sa" ? (
        <>
          <Space.Compact className="mt-4">
            <KindSelect
              style={{ width: 450 }}
              kind={"serviceaccount"}
              value={serviceAccountValue}
              onChange={(value) => {
                setServiceAccountValue(value);
                setServiceAccName(value);
              }}
              optionValue="name"
            />
            <Tooltip title={`Create Service Account`}>
              <Button
                icon={<PlusCircle className="h-4" />}
                onClick={() => {
                  drawer.setPersistent(
                    "mk8s-kubeconfig-create-serviceaccount",
                    "Create Service Account",
                    <EmbeddedServiceAccountCreate
                      onCancel={() => drawer.closePersistent()}
                      onDone={(itemLink) => {
                        drawer.closePersistent();
                        const { name } = parseLink(itemLink, { useInputCtx: true });
                        setServiceAccountValue(name);
                        setServiceAccName(name);
                      }}
                    />,
                    600,
                  );
                }}
              />
            </Tooltip>
          </Space.Compact>
          <div className="flex items-center gap-1 text-sm mt-2 mb-4">
            <AlertCircle className={`feather-icon color-primary`} />
            <div>Create or select a service account. A new key will be created to be used for the kubeconfig file.</div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              disabled={!serviceAccName || isLoading}
              loading={isLoading}
              type="primary"
              style={{ width: 450 }}
              onClick={() => createKubeConfig()}
            >
              Download kubeconfig
            </Button>
          </div>
        </>
      ) : null}
      <div
        className={`${method === "sa" ? "mt-8" : "mt-2"} color-link cursor-pointer flex items-center gap-1`}
        onClick={() => setMethod((v) => (v === "pr" ? null : "pr"))}
      >
        <FormLabel>Continue with CPLN CLI Profile</FormLabel>
        {method === "pr" ? (
          <ChevronUp style={{ width: 16, height: 16 }} />
        ) : (
          <ChevronDown style={{ width: 16, height: 16 }} />
        )}
      </div>
      {method === "pr" ? (
        <>
          <div className="flex flex-col gap-2 my-4">
            <FormLabel>CPLN CLI Profile Name</FormLabel>
            <Input
              style={{ width: 450 }}
              placeholder="default"
              value={profileName}
              onChange={(e) => setProfileName(e.target.value)}
            />
          </div>
          <div className="flex items-center gap-1 text-sm my-2">
            <AlertCircle className={`feather-icon color-primary`} />
            <div>CPLN command line tool needs to be installed for this method to work.</div>
          </div>
          <ExternalLink label="How to Install CPLN CLI?" url={`${DOCS_URL}/reference/cli`} disableCopy />
          <div className="flex items-center gap-2 mt-4">
            <Button
              disabled={isLoading}
              loading={isLoading}
              type="primary"
              style={{ width: 450 }}
              onClick={() => createKubeConfigForProfile()}
            >
              Download kubeconfig
            </Button>
          </div>
        </>
      ) : null}
    </div>
  );
};
